// Small, pure changes to editor items: nudging, resizing and bounds.
import type { Item } from "@/lib/pdf/items";

type Size = { width: number; height: number };

/** The rough box an item covers on its page (for nudging and selection). */
export function boundsOf(item: Item): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  switch (item.kind) {
    case "text": {
      const lines = item.text.split("\n");
      const longest = Math.max(...lines.map((l) => l.length), 1);
      return {
        x: item.x,
        y: item.y,
        width: longest * item.size * 0.55,
        height: lines.length * item.size * 1.2,
      };
    }
    case "line":
    case "pen":
    case "highlight": {
      const xs = item.points.filter((_, i) => i % 2 === 0);
      const ys = item.points.filter((_, i) => i % 2 === 1);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);
      return {
        x: item.x + minX,
        y: item.y + minY,
        width: Math.max(...xs) - minX,
        height: Math.max(...ys) - minY,
      };
    }
    default:
      return { x: item.x, y: item.y, width: item.width, height: item.height };
  }
}

/** Moves an item by (dx, dy) points, keeping at least part of it on the page. */
export function nudge(item: Item, dx: number, dy: number, page: Size): Item {
  const b = boundsOf(item);
  const keep = 8; // points that must stay visible
  const nx = Math.min(Math.max(b.x + dx, keep - b.width), page.width - keep);
  const ny = Math.min(Math.max(b.y + dy, keep - b.height), page.height - keep);
  return { ...item, x: item.x + (nx - b.x), y: item.y + (ny - b.y) };
}

/** Bakes a resize (scale from the item's top-left) into its size, so saved
 *  items never carry a separate scale. Text keeps its shape: it scales by
 *  the vertical factor. */
export function resize(item: Item, sx: number, sy: number): Item {
  switch (item.kind) {
    case "text":
      return {
        ...item,
        size: Math.max(4, Math.round(item.size * sy * 10) / 10),
      };
    case "line":
    case "pen":
    case "highlight":
      return {
        ...item,
        points: item.points.map((v, i) => (i % 2 === 0 ? v * sx : v * sy)),
      };
    default:
      return {
        ...item,
        width: Math.max(4, item.width * sx),
        height: Math.max(4, item.height * sy),
      };
  }
}

let counter = 0;
export function newId(): string {
  counter += 1;
  return `item-${Date.now().toString(36)}-${counter}`;
}
